import type { UseFormSetValue } from "react-hook-form";
import type { Geofence } from "../../../../../../../store/admin/geofencesStore";
import type { ThemeClasses, PointCoordinate } from "../../types";
import { CoordinateInput } from "./CoordinateInput";

interface PolygonalCoordinatesFormProps {
  polygonPoints: PointCoordinate[];
  setPolygonPoints: (points: PointCoordinate[]) => void;
  setValue: UseFormSetValue<Geofence>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
}

export function PolygonalCoordinatesForm({
  polygonPoints,
  setPolygonPoints,
  setValue,
  themeClasses,
  isDarkMode,
}: PolygonalCoordinatesFormProps) {
  const commitPoints = (newPoints: PointCoordinate[]) => {
    setPolygonPoints(newPoints);
    setValue("coordinates", newPoints);
  };

  const handleRemovePoint = (idx: number) => {
    commitPoints(polygonPoints.filter((_, i) => i !== idx));
  };

  const handleChangeCoordinate = (
    idx: number,
    field: "latitude" | "longitude",
    next: number
  ) => {
    commitPoints(
      polygonPoints.map((p, i) => (i === idx ? { ...p, [field]: next } : p))
    );
  };

  const handleAddPoint = () => {
    const last = polygonPoints[polygonPoints.length - 1];
    commitPoints([
      ...polygonPoints,
      {
        latitude: last ? last.latitude + 0.002 : 19.4326,
        longitude: last ? last.longitude + 0.002 : -99.1332,
      },
    ]);
  };

  return (
    <div className="flex flex-col gap-3">
      <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
        Puntos del polígono
      </label>

      {/* Contenedor con scroll */}
      <div
        className={`max-h-60 overflow-y-auto pr-2 space-y-2 ${
          isDarkMode ? "scrollbar-dark" : "scrollbar-light"
        }`}
      >
        {polygonPoints.map((point, idx) => (
          <div
            key={idx}
            className={`flex gap-2 items-center p-2 rounded-lg ${
              isDarkMode ? "bg-dark-card" : "bg-gray-50"
            } border ${themeClasses.cardBorder}`}
          >
            {/* Número de punto */}
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                isDarkMode
                  ? "bg-dark-border text-gray-300"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {idx + 1}
            </div>

            {/* Coordenada Latitud */}
            <div className="flex-1 min-w-0">
              <label
                className={`w-full text-xs 2xl:text-base px-3 py-2 ${
                  isDarkMode ? "bg-dark-surface" : "bg-white"
                } border ${themeClasses.cardBorder} rounded-lg ${
                  themeClasses.text
                } flex items-center gap-2 focus-within:ring-2 focus-within:ring-primary-500`}
              >
                <span className="text-gray-500">Lat:</span>
                <CoordinateInput
                  value={point.latitude}
                  onChange={(next) => handleChangeCoordinate(idx, "latitude", next)}
                  className="flex-1 min-w-0 w-full bg-transparent outline-none"
                />
              </label>
            </div>

            {/* Coordenada Longitud */}
            <div className="flex-1 min-w-0">
              <label
                className={`w-full text-xs 2xl:text-base px-3 py-2 ${
                  isDarkMode ? "bg-dark-surface" : "bg-white"
                } border ${themeClasses.cardBorder} rounded-lg ${
                  themeClasses.text
                } flex items-center gap-2 focus-within:ring-2 focus-within:ring-primary-500`}
              >
                <span className="text-gray-500">Lng:</span>
                <CoordinateInput
                  value={point.longitude}
                  onChange={(next) => handleChangeCoordinate(idx, "longitude", next)}
                  className="flex-1 min-w-0 w-full bg-transparent outline-none"
                />
              </label>
            </div>

            {/* Botón eliminar */}
            <button
              type="button"
              onClick={() => handleRemovePoint(idx)}
              className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                polygonPoints.length <= 3
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 hover:text-red-600 dark:hover:text-red-400"
              }`}
              disabled={polygonPoints.length <= 3}
              title="Eliminar punto"
            >
              <i className="ri-delete-bin-line text-lg"></i>
            </button>
          </div>
        ))}
      </div>

      {/* Botones de acción */}
      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={handleAddPoint}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-400 text-white rounded-lg font-medium transition-colors"
        >
          <i className="ri-add-line"></i>
          Agregar punto
        </button>

        {polygonPoints.length < 3 && (
          <span className="text-red-500 text-xs">Mínimo 3 puntos</span>
        )}
      </div>
    </div>
  );
}
